import { ChevronRight, Printer } from 'react-feather';
import PageLayout from '@/components/PageLayout';
import { formatCurrency, getQueryString } from '@/utils/common.util';
import useSWR from 'swr';
import { useState, useEffect } from 'react';
import {
  DatePicker,
  DatePickerInput,
  Select,
  SelectItem,
} from 'carbon-components-react';
import PrintOutHead from '@/components/PrintOutHead';
import { PrintView } from '@/components/PrintView/print-view.component';
import CustomButton from '@/components/custom-button/custom-btn.component';
import { useUrlState } from '@/utils/useUrlState';
import ExportReportButton from '@/components/ExportReportButton';
import Expandable from '@/components/expandable/Expandable/Expandable';
import './students-termly-arrears.styles.scss';

const initialData = {
  dateFrom: '',
  dateTo: '',
  academicYearId: '',
  termId: '',
  classId: '',
};

export default function StudentsTermlyArrearsReportPage() {
  const pageTitle = `Students Termly Arrears Report`;

  const [query, setQuery] = useUrlState(initialData);
  const [allExpanded, setAllExpanded] = useState(false);

  const { data: academicYears } = useSWR(
    'academic-year/list/?skip=0&take=100&query=',
  );
  const { data: terms } = useSWR(
    query.academicYearId
      ? `academic-year/terms/?academicYearId=${query.academicYearId}`
      : null,
  );
  const { data: classes } = useSWR('class/list');

  const mainUrl = `analytics/termly-arrears-report`;
  const { data, isLoading, error } = useSWR(
    query.academicYearId && query.termId
      ? `${mainUrl}?${getQueryString(query)}`
      : null,
  );

  const classGroups = data?.classGroups ?? [];

  // get all data from the data object except the classGroups
  const metaInfo = data
    ? Object.fromEntries(
        Object.entries(data).filter(([key]) => key !== 'classGroups'),
      )
    : {};

  const grandTotalStudents =
    data?.classGroups?.reduce((acc, curr) => acc + curr.studentCount, 0) ?? 0;

  const grandTotals = [
    { label: 'Total Students', value: grandTotalStudents },
    {
      label: 'Opening Balance (GH₵)',
      value: formatCurrency(data?.grandTotalOpeningBalance),
    },
    {
      label: 'Terms Bill (GH₵)',
      value: formatCurrency(data?.grandTotalTermsBill),
    },
    { label: 'Balance (GH₵)', value: formatCurrency(data?.grandTotalBalance) },
    {
      label: 'Payments (GH₵)',
      value: formatCurrency(data?.grandTotalPayments),
    },
    {
      label: 'Closing Balance (GH₵)',
      value: formatCurrency(data?.grandTotalClosingBalance),
    },
  ];

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Analytics', to: '/analytics' },
        { label: pageTitle, active: true },
      ]}
      title={pageTitle}
      isLoading={isLoading}
    >
      {/* FILTERS  */}
      <p className="mt-20 text-12">
        Select at least an academic year and term to show report.
      </p>
      <div className={`mt-10 card-row-5`}>
        <Select
          labelText="Academic Year"
          onChange={(e) =>
            setQuery({ academicYearId: e.target.value, termId: '' })
          }
          value={query.academicYearId}
        >
          <SelectItem value="" text="All" />
          {academicYears?.map((year) => (
            <SelectItem key={year.id} value={year.id} text={year.description} />
          ))}
        </Select>
        <Select
          labelText="Term"
          disabled={!query.academicYearId}
          onChange={(e) => setQuery({ termId: e.target.value })}
          value={query.termId}
        >
          <SelectItem value="" text="All" />
          {terms?.map((term) => (
            <SelectItem key={term.id} value={term.id} text={term.description} />
          ))}
        </Select>
        <Select
          labelText={'Class'}
          onChange={(e) => setQuery({ classId: e.target.value })}
          value={query.classId}
        >
          <SelectItem value="" text="All" />
          {classes?.map((term) => (
            <SelectItem key={term.id} value={term.id} text={term.description} />
          ))}
        </Select>
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="start-date-picker"
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                dateFrom: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ dateFrom: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="start-date"
            id="start-date"
            name="dateFrom"
            labelText="Start Date"
            value={query.dateFrom}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ dateFrom: e.target.value })}
          />
        </DatePicker>
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="end-date-picker"
          // minDate={
          //   new Date(query.dateFrom ?? '').toISOString().split('T')[0]
          // }
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                dateTo: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ dateTo: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="end-date"
            id="end-date"
            name="dateTo"
            labelText="End Date"
            value={query.dateTo}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ dateTo: e.target.value })}
          />
        </DatePicker>
      </div>

      <div className="mt-20">
        {error && <p className="text-14 mt-10 text-danger">{error.message}</p>}
        {classGroups?.length > 0 && (
          <>
            <div className="d-flex flex-end">
              <CustomButton
                classes="secondary"
                onClick={() => setAllExpanded(!allExpanded)}
              >
                {allExpanded ? 'Collapse All' : 'Expand All'}
              </CustomButton>
            </div>

            <PrintView>
              <div style={{ overflow: 'auto' }}>
                {classGroups.map((group) => (
                  <GroupSection
                    key={group.classId}
                    groupData={group}
                    expanded={allExpanded}
                    metaInfo={metaInfo}
                  />
                ))}

                {/* Grand totals */}
                <div className="align-items-center grid-col-row py-12 px-16 bg-primary text-white rounded-4 mt-30">
                  <p className="font-bold">Grand Total</p>

                  <div className="totals-row">
                    {grandTotals?.map((total) => (
                      <div
                        key={total.label}
                        className="grid gap-10 text-center"
                      >
                        <span className="text-10">{total.label}</span>
                        <span className="font-bold text-18">{total.value}</span>
                      </div>
                    ))}
                  </div>
                  <div></div>
                </div>
              </div>
            </PrintView>
          </>
        )}
        {classGroups?.length === 0 && (
          <p className="text-14 mt-30 text-grey">No data available.</p>
        )}

        {classGroups?.length > 0 && (
          <div className="mt-30 d-flex gap-20 align-items-center flex-end">
            <ExportReportButton
              apiUrl={`${mainUrl}/export?${getQueryString(query)}`}
              fileName={`${pageTitle}.xlsx`}
            />

            <CustomButton classes="secondary" onClick={() => window.print()}>
              <Printer size="16" strokeWidth="2px" /> &nbsp; Print
            </CustomButton>
          </div>
        )}
      </div>
    </PageLayout>
  );
}

const GroupSection = ({ groupData, expanded, metaInfo }) => {
  const [internalIsExpanded, setInternalIsExpanded] = useState(false);

  // Update internal state when expanded prop changes (for expand/collapse all)
  useEffect(() => {
    if (expanded !== undefined) {
      setInternalIsExpanded(expanded);
    }
  }, [expanded]);

  const isExpanded = internalIsExpanded;

  const totals = [
    { label: 'No. of Students', value: groupData.studentCount },
    {
      label: 'Opening Balance (GH₵)',
      value: formatCurrency(groupData.totalOpeningBalance),
    },
    {
      label: "Term's Bill (GH₵)",
      value: formatCurrency(groupData.totalTermsBill),
    },
    {
      label: 'Total Balance (GH₵)',
      value: formatCurrency(groupData.totalBalance),
    },
    { label: 'Payments (GH₵)', value: formatCurrency(groupData.totalPayments) },
    {
      label: 'Closing Balance (GH₵)',
      value: formatCurrency(groupData.totalClosingBalance),
    },
  ];

  return (
    <div
      style={{ breakAfter: 'page' }}
      className="mt-30 bg-white p-16 rounded-4"
    >
      <PrintOutHead
        noPrintView
        title={`Students Termly Arrears Report`}
        headings={[
          {
            label: 'Academic Year',
            value: metaInfo.academicYear ?? '-',
          },
          {
            label: 'Term',
            value: metaInfo.term ?? '-',
          },
        ]}
      />
      <div className="align-items-center grid-col-row">
        <div className="">
          <p className="font-bold">{groupData.className}</p>
          {isExpanded && (
            <p>
              No. of Students:{' '}
              <span className="font-bold">{groupData.studentCount}</span>
            </p>
          )}
        </div>
        <div className="totals-row">
          {totals?.map((total, index) => (
            <div
              key={index + 1}
              className="grid gap-10 text-center no-print"
              style={{
                opacity: isExpanded ? 0 : 1,
                transition: 'opacity 0.3s',
              }}
            >
              <span className="text-10 text-grey">{total.label}</span>
              <span className="font-bold text-18">{total.value}</span>
            </div>
          ))}
        </div>

        <button
          className="clean-btn expand-btn"
          onClick={() => setInternalIsExpanded(!internalIsExpanded)}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          title={isExpanded ? 'Collapse' : 'Expand'}
        >
          <ChevronRight
            size={12}
            strokeWidth={5}
            style={{
              rotate: isExpanded ? '90deg' : '0deg',
              transition: 'rotate 0.3s',
            }}
          />
        </button>
      </div>
      <Expandable isExpanded={isExpanded} className="mt-10">
        <table className="bx--data-table table-white">
          <thead>
            <tr>
              <th>Student ID</th>
              <th>Student Name</th>
              <th>Opening Balance (GH₵)</th>
              <th>Term's Bill (GH₵)</th>
              <th>Total Balance (GH₵)</th>
              <th>Payments (GH₵)</th>
              <th>Closing Balance (GH₵)</th>
            </tr>
          </thead>
          <tbody>
            {groupData.students.map((entry) => {
              return (
                <tr key={entry.studentId}>
                  <td>{entry.studentNumber}</td>
                  <td>{entry.studentName}</td>
                  <td>{formatCurrency(entry.openingBalance)}</td>
                  <td>{formatCurrency(entry.termsBill)}</td>
                  <td>{formatCurrency(entry.totalBalance)}</td>
                  <td>{formatCurrency(entry.payments)}</td>
                  <td>{formatCurrency(entry.closingBalance)}</td>
                </tr>
              );
            })}
            {/* tfoot - its this way cos of printing */}
            <tr className="font-bold">
              <td>Total</td>
              <td>
                <span className="print-only">{groupData.studentCount}</span>
              </td>
              <td>{formatCurrency(groupData.totalOpeningBalance)}</td>
              <td>{formatCurrency(groupData.totalTermsBill)}</td>
              <td>{formatCurrency(groupData.totalBalance)}</td>
              <td>{formatCurrency(groupData.totalPayments)}</td>
              <td>{formatCurrency(groupData.totalClosingBalance)}</td>
            </tr>
          </tbody>
        </table>
      </Expandable>
    </div>
  );
};
